import React, { memo, useCallback, useContext, useState } from 'react';
import Animated, { runOnJS, useAnimatedReaction, useAnimatedStyle, useDerivedValue } from 'react-native-reanimated';

import { CircularPagerContext } from './CircularPager';
import { CIRCULAR_LENGTH, checkIfSheetIsActive, wrapToHalfLength } from './circular-helpers';

type Props = {
    offset: number;
};

/**
 * This component reads all relevant data from the CircularPagerContext,
 * Plus the offset prop, to minimize re-renders and avoid flickering.
 *
 * This component computes necessary animations/props and passes them to the renderPage function, such as:
 * - isActive: if the current page is the one visible on the screen
 * - shouldFreeze: if the page should be frozen (used with the Freeze component);
 *                 this props is passed to the second neighbor page in each direction
 * - focusAnim: the animation value that indicates how far the page is from the center
 *              can be used to animate page content or other elements
 * - pageAnim: the animation value that indicates the current page index as a continuous value
 *             it's a floating number and represents the swipe position between pages
 * - currentIndex: the current page index
 *
 */
export const PageSlotContent = memo(function PageSlotContent({ offset }: Props) {
    const pagerContext = useContext(CircularPagerContext);
    if (!pagerContext) {
        throw new Error('CircularPagerContext is not available');
    }

    const { pageAnim, renderPage, currentPageIndex, nextActivePage } = pagerContext;

    // "focusAnim" is (offset - pageAnim.value), wrapped to [-2.5..2.5]
    const focusAnim = useDerivedValue(() => {
        const rawDiff = offset - pageAnim.value;
        return wrapToHalfLength(rawDiff);
    });

    // we need animated shared values to animate the page content with no state update/delays
    const shouldFreezeAnimatedValue = useDerivedValue(() => Math.abs(focusAnim.value) > 1.5);

    // we need JS state values to trigger re-renders in the pages
    const [isActive, setIsActive] = useState(Math.abs(focusAnim.value) < 0.5);
    const [shouldFreeze, setShouldFreeze] = useState(shouldFreezeAnimatedValue.value);

    const setData = useCallback((nextIsActive: boolean, nextShouldFreeze: boolean) => {
        setIsActive(nextIsActive);
        setShouldFreeze(nextShouldFreeze);
    }, []);

    useAnimatedReaction(
        () => {
            // "active" if inside ±0.5 from center or if active target is the current page
            const nextIsActive = checkIfSheetIsActive({ focusAnim, nextActivePage, offset });
            // "freeze" if outside ±1.5
            // we also freeze if the user used setPage to change the page
            const nextShouldFreeze = shouldFreezeAnimatedValue.value && !nextIsActive;

            return { nextIsActive, nextShouldFreeze };
        },
        (derived, prev) => {
            if (
                !prev ||
                derived.nextIsActive !== prev.nextIsActive ||
                derived.nextShouldFreeze !== prev.nextShouldFreeze
            ) {
                runOnJS(setData)(derived.nextIsActive, derived.nextShouldFreeze);
            }
        }
    );

    // we ensure that frozen content is not visible if the user swipes really fast
    // this is a safe guard mostly, I was not able to see any gaps in practice
    const animatedStyle = useAnimatedStyle(() => {
        return {
            opacity: shouldFreezeAnimatedValue.value ? 0 : 1,
        };
    });

    return (
        <Animated.View style={animatedStyle}>
            {renderPage({
                offset: offset,
                isActive,
                shouldFreeze,
                length: CIRCULAR_LENGTH,
                focusAnim,
                pageAnim,
                currentIndex: currentPageIndex,
            })}
        </Animated.View>
    );
});
